import { Button, Modal, Stack, Text } from "@assetwatch/ui";
import {
  type EditDraft,
  findEditDraftConflicts,
} from "../snowflakes/work-orders/WorkOrderEditForm";

export type RefreshedWorkOrderDraft = {
  session: number;
  baseline: EditDraft;
  latest: EditDraft;
  attachment: { attachmentId: number | null; procedureId: string | null };
};

const labels: Record<keyof EditDraft, string> = {
  version: "Version",
  title: "Title",
  assetId: "Asset",
  categoryIds: "Categories",
  description: "Description",
  instructions: "Instructions",
  priorityCode: "Priority",
  statusCode: "Status",
  workTypeCode: "Work type",
  plannerGroupCode: "Planner group",
  startDate: "Start date",
  dueDate: "Due date",
  assignees: "Assigned to",
  procedureId: "Procedure",
};

function displayValue(value: EditDraft[keyof EditDraft]): string {
  return Array.isArray(value)
    ? value.join(", ") || "None"
    : String(value ?? "None") || "None";
}

/** Keep the draft unchanged until the user reviews the refreshed record. */
export function WorkOrderDraftRefreshDialog({
  draft,
  refresh,
  onResolve,
  onCancel,
}: {
  draft: EditDraft;
  refresh: RefreshedWorkOrderDraft;
  onResolve: (resolution: "mine" | "latest") => void;
  onCancel: () => void;
}) {
  const conflicts = findEditDraftConflicts(
    draft,
    refresh.baseline,
    refresh.latest,
  );
  return (
    <Modal
      title="Review refreshed Work Order"
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <div>
        <Stack gap={16}>
          <Text>
            {conflicts.length
              ? "These fields were changed by both you and another user. Choose which values to keep. Your other edits will be preserved."
              : "The latest Work Order is ready. Continue with your edits preserved and untouched fields updated."}
          </Text>
          {conflicts.map((key) => (
            <Stack gap={4} key={key}>
              <Text>{labels[key]}</Text>
              <Text size="sm">Your edit: {displayValue(draft[key])}</Text>
              <Text size="sm">
                Latest value: {displayValue(refresh.latest[key])}
              </Text>
            </Stack>
          ))}
        </Stack>
      </div>
      <div className="flex flex-wrap justify-end gap-(--spacing-8)">
        <Button variant="outline" onClick={onCancel}>
          Back to editing
        </Button>
        {conflicts.length ? (
          <Button variant="outline" onClick={() => onResolve("latest")}>
            Use latest conflicting values
          </Button>
        ) : null}
        <Button onClick={() => onResolve("mine")}>
          {conflicts.length ? "Keep my conflicting edits" : "Continue editing"}
        </Button>
      </div>
    </Modal>
  );
}
